import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './components/ui';
import Layout from './components/Layout';
import BackButtonHandler from './components/BackButtonHandler';
import BackKeyHandler from './components/BackKeyHandler';
import DeviceGuard from './components/DeviceGuard';
import { mainPath } from './utils/backStep';
import DevReloadBar from './components/DevReloadBar';
import ContactAutoSync from './components/ContactAutoSync';
import SaveKeyHandler from './components/SaveKeyHandler';
import OtaUpdater from './components/OtaUpdater';
import ForceUpdateGuard from './components/ForceUpdateGuard';
import Login from './pages/login';
import Register from './pages/register';
import OTP from './pages/otp';
import ForgotPassword from './pages/forgotPassword';
import GuestHome from './pages/guestHome';
import { getGuestSession } from './utils/guestSession';
import ChangePassword from './pages/changePassword';
import Invite from './pages/invite';
import GuestCard from './pages/guestCard';
import Dashboard from './pages/dashboard';
import Events from './pages/events';
import Users from './pages/users';
import Functions from './pages/functions';
import Members from './pages/members';
import Setup from './pages/setup';
import Profile from './pages/profile';
import Status from './pages/status';
import Headcount from './pages/headcount';
import Brief from './pages/brief';
import Reminders from './pages/reminders';
import Services from './pages/services';
import Expenses from './pages/expenses';
import ServiceDetail from './pages/serviceDetail';
import MyWork from './pages/myWork';
import Cards from './pages/cards';
import Labels from './pages/labels';
import LabelSetup from './pages/labelSetup';
import Families from './pages/families';
import InviteForms from './pages/inviteForms';
import UserManagement from './pages/userManagement';
import ShareApp from './pages/shareApp';

function Protected({ children }) {
  const { user, ready, mustChangePassword } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  // Default password '1234' → nothing works until it is changed (all platforms).
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  return children;
}

// /change-password itself: signed in, but NOT blocked by the must-change rule.
function SignedIn({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

// Auth pages (Login / OTP / Register): once a session is saved in localStorage,
// never show these again — go straight to the app. This is what stops the back
// button (or a re-open) from landing on the OTP/login screen while signed in.
function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/app" replace />;
  // A signed-in invited guest goes straight back to their guest screen.
  if (getGuestSession()) return <Navigate to="/guest" replace />;
  return children;
}

// Plain users land on Members (their only screen); admins land on the Dashboard.
// Where the app opens - ONE rule, shared with the back ladder (utils/backStep
// mainPath). Card distribution started on 2026-10-26, so whoever can see the
// guest list lands on their own round of cards.
function Home() {
  const { user, isAdmin, can } = useAuth();
  return <Navigate to={mainPath({ signedIn: !!user, isAdmin, canGuests: can('CanMembers') })} replace />;
}

function Access({ adminOnly = false, superAdminOnly = false, children }) {
  const { isAdmin, isSuperAdmin } = useAuth();
  const allowed = superAdminOnly ? isSuperAdmin : adminOnly ? isAdmin : true;
  if (!allowed) return <Navigate to="/app" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        {/* The v7 behaviours are already what this app expects, so opt in now
            and keep the console clear of the upgrade warnings. */}
        <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <BackButtonHandler />
          <BackKeyHandler />
          <SaveKeyHandler />
          <DeviceGuard />
          <ContactAutoSync />
          <OtaUpdater />
          <ForceUpdateGuard />
          {process.env.NODE_ENV === 'development' && <DevReloadBar />}
          <Routes>
            <Route path="/" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/otp" element={<PublicOnly><OTP /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/change-password" element={<SignedIn><ChangePassword /></SignedIn>} />
            <Route path="/invite" element={<Invite />} />
            {/* The QR code on an invitation label: /g/<uuid>, no sign-in. */}
            <Route path="/g/:uuid" element={<GuestCard />} />
            <Route path="/guest" element={<GuestHome />} />
            <Route path="/app" element={<Protected><Layout /></Protected>}>
              <Route index element={<Home />} />
              <Route path="dashboard" element={<Access adminOnly><Dashboard /></Access>} />
              <Route path="events" element={<Access adminOnly><Events /></Access>} />
              <Route path="setup" element={<Access adminOnly><Setup /></Access>} />
              <Route path="functions" element={<Access adminOnly><Functions /></Access>} />
              <Route path="members" element={<Members />} />
              <Route path="invite-forms" element={<InviteForms />} />
              <Route path="status" element={<Status />} />
              <Route path="reminders" element={<Reminders />} />
              <Route path="services" element={<Services />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="services/:serviceId" element={<ServiceDetail />} />
              <Route path="my-work" element={<MyWork />} />
              <Route path="cards" element={<Cards />} />
              <Route path="labels" element={<Labels />} />
              <Route path="label-setup" element={<Access adminOnly><LabelSetup /></Access>} />
              <Route path="families" element={<Families />} />
              <Route path="headcount" element={<Access adminOnly><Headcount /></Access>} />
              <Route path="brief" element={<Access adminOnly><Brief /></Access>} />
              <Route path="users" element={<Users />} />
              <Route path="user-management" element={<Access superAdminOnly><UserManagement /></Access>} />
              <Route path="profile" element={<Profile />} />
              <Route path="share-app" element={<ShareApp />} />
              <Route path="guests" element={<Navigate to="/app/members" replace />} />
              <Route path="arrangements" element={<Navigate to="/app" replace />} />
              <Route path="team" element={<Navigate to="/app" replace />} />
              <Route path="vendors" element={<Navigate to="/app" replace />} />
              <Route path="payments" element={<Navigate to="/app" replace />} />
              <Route path="tasks" element={<Navigate to="/app" replace />} />
              <Route path="rsvp" element={<Navigate to="/app" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </HashRouter>
      </ToastProvider>
    </AuthProvider>
  );
}
